import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'

import { useFarmContext } from 'src/Elements/Farm'
import { fetchWallboxPrices } from 'src/Libs/e-mobility/emobility-wallbox/api/fetch-wallbox-prices.api'
import { fetchWallboxesByStorage, fetchWallboxesByStorageInBulk } from 'src/Libs/wallboxes/api/wallboxes.api'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { fetchChargingSessions } from '../api/e-mobility-charging-session.api'
import { ChargingSessionModelV1 } from '../models/charging-session.model'
import { usePortalQueries, usePortalQuery } from 'src/Libs/api'

interface UseChargingSessionsHook {
  chargingSessions: ChargingSessionModelV1[] | undefined
  wallboxes: WallboxHardwareModel[] | undefined
  isLoading: boolean
  isError: boolean
}

export const useChargingSessionsV1 = (storageId: string): UseChargingSessionsHook => {
  const authFetch = useAuthFetchWithResult()
  const { isFarm, farmClients, isLoading: isFarmLoading } = useFarmContext()

  const farmStorages = farmClients?.map(client => client.storageId) || []
  const storagesToFetchFrom = isFarm ? farmStorages : [storageId]

  const { data: wallboxes, isLoading: wbLoading } = usePortalQuery({
    queryFn: () => (isFarm ? fetchWallboxesByStorageInBulk(farmStorages, authFetch) : fetchWallboxesByStorage(storageId, authFetch)),
    queryKey: ['wallboxes', ...storagesToFetchFrom],
    enabled: !isFarmLoading,
  })

  const chargePricesQueries = usePortalQueries(
    storageId && wallboxes
      ? wallboxes.map(wallbox => ({
          queryKey: ['chargePrices', wallbox],
          queryFn: () => {
            return fetchWallboxPrices(wallbox.storageSerialNumber.toString(), wallbox.number, 'charge', authFetch)
          },
        }))
      : []
  )

  const dischargePricesQueries = usePortalQueries(
    storageId && wallboxes
      ? wallboxes.map(wallbox => ({
          queryKey: ['dischargePrices', wallbox],
          queryFn: () => {
            return fetchWallboxPrices(wallbox.storageSerialNumber.toString(), wallbox.number, 'discharge', authFetch)
          },
        }))
      : []
  )

  const chargePrices = chargePricesQueries.map(res => {
    return res.data ?? undefined
  })

  const dischargePrices = dischargePricesQueries.map(res => {
    return res.data ?? undefined
  })

  /**
   * The endpoint for fetching charging sessions does not return all data that is shown in the UI.
   *
   * Charge prices, discharge prices and wallbox names must be fetched from additional endpoints. For better development
   * experience, we encapsulate these multiple fetches into a single hook and do the mapping and aggregation inside it.
   *
   * To fetch prices for each wallbox by wallbox id, we must first fetch the wallboxes connected to the installation. However, to decide if we should fetch wallboxes
   * in bulk for multiple storages at once(farm) or wallboxes for a single installation, we must fetch information if this installation is
   * a farm or not(another fetch).
   *
   * So, for this reason, we consider these fetches "dependencies" and postpone fetching sessions until all dependencies are fetched.
   */
  const dependenciesLoading =
    chargePricesQueries.some(query => query.isLoading) || dischargePricesQueries.some(query => query.isLoading) || wbLoading || isFarmLoading

  const sessionQueries = usePortalQueries(
    storagesToFetchFrom.map(storage => {
      return {
        queryFn: () => fetchChargingSessions(storage, chargePrices, dischargePrices, wallboxes || [], authFetch),
        queryKey: ['chargingSessions', storage, chargePrices, dischargePrices, wallboxes],
        refetchOnMount: true,
        enabled: !dependenciesLoading,
      }
    })
  )

  const chargingSessionsLoading = sessionQueries.some(queryResult => queryResult.isLoading)
  const isLoading = chargingSessionsLoading || dependenciesLoading
  const isError = sessionQueries.some(queryResult => queryResult.isError)

  const mergedSessions = sessionQueries.flatMap(queryResult => (queryResult.data ? queryResult.data : []))

  return {
    chargingSessions: mergedSessions,
    wallboxes: wallboxes ?? undefined,
    isLoading,
    isError,
  }
}
